import type { components } from "../../../api/generated";
import type { OpportunityCustomizationProjectItem } from "../../crm_opportunity_workbench/types";
import type {
  CreateCustomizationDemandPayload,
  CreateCustomizationDemandResult,
  CreateCustomizationProjectWithTraceResult,
  CustomizationChangedField,
  CustomizationCollegeCandidate,
  CustomizationContactCandidate,
  CustomizationCurrentUserTraceRole,
  CustomizationCustomerCandidate,
  CustomizationDemandCustomerRequirementsSection,
  CustomizationDemandDraft,
  CustomizationDemandFieldOptions,
  CustomizationDemandDisplayField,
  CustomizationDemandDisplayGroup,
  CustomizationDemandRelationsSection,
  CustomizationDemandSummarySection,
  CustomizationDemandTeacherRequirementsSection,
  CustomizationDemandVersionOption,
  CustomizationDemandVersionWithoutPlanItem,
  CustomizationDemandVersionWithoutPlanPage,
  CustomizationDemandVersionWithoutPlanParams,
  CustomizationDispatcherGroupDetailItem,
  CustomizationDispatcherOrderDetailItem,
  CustomizationDispatcherSelectionCandidate,
  CustomizationDispatcherNotificationPayload,
  CustomizationDispatcherNotificationResult,
  CustomizationMajorCandidate,
  CustomizationMyWorkloadDetailType,
  CustomizationMyWorkloadPayload,
  CustomizationOptions,
  CustomizationOpportunityContext,
  CustomizationOpportunityBinding,
  CustomizationOpportunityBindingDecision,
  CustomizationProjectEditOptions,
  CustomizationProjectEditOptionsParams,
  CustomizationProjectFieldOptions,
  CustomizationProjectDemandVersionList,
  CustomizationProjectItineraryList,
  CustomizationProjectListItem,
  CustomizationProjectListPage,
  CustomizationProjectListParams,
  CustomizationProjectPointNameOptions,
  CustomizationProjectPointNameOptionsParams,
  CustomizationProjectUpdatePayload,
  CustomizationLegacyRoutePreview,
  CustomizationItineraryGenerationValidationResult,
  LegacyCustomizeRouteImportPayload,
  CustomizationReferenceDemandCandidate,
  CustomizationReferenceDemandList,
  CustomizationRouteLinkRecognitionResult,
  CustomizationReferenceUserCandidate,
  CustomizationServiceStandardDraft,
  CustomizationTraceCreate,
  CustomizationTraceItem,
  CustomizationWorkbench,
} from "../types";

type Schemas = components["schemas"];

export type CustomizationWorkbenchRaw = Schemas["CustomizationWorkbench"];
export type CustomizationOpportunityBindingRaw = Schemas["CustomizationOpportunityBinding"];
export type CustomizationOpportunityBindingDecisionRaw =
  Schemas["CustomizationOpportunityBindingDecision"];
export type CustomizationTraceItemRaw = Schemas["CustomizationTraceItem"];
export type CreateDemandResultRaw = Schemas["CreateDemandResult"];
export type CustomizationProjectListPageRaw = Schemas["CustomizationProjectListPage"];
export type CustomizationProjectDemandVersionListRaw = {
  items?: Schemas["CustomizationDemandVersionOption"][];
};
export type CustomizationDemandVersionWithoutPlanItemRaw = {
  id?: string;
  project_id?: string;
  project_code?: string;
  project_name?: string;
  legacy_id?: string | null;
  operating_company?: string | null;
  sales_user_name?: string | null;
  dispatcher_user_name?: string | null;
  customer_type?: string | null;
  version_number?: string;
  version_seq?: number;
  created_at?: string;
  order_version_key?: string | null;
  departure_start_date?: string | null;
  student_count?: number | null;
  teacher_count?: number | null;
  province_names?: string[];
  stay_area_names?: string[];
  point_names?: string[];
  has_itinerary?: boolean;
  itinerary_count?: number;
  finalized_budget_count?: number;
};
export type CustomizationDemandVersionWithoutPlanPageRaw = {
  items?: CustomizationDemandVersionWithoutPlanItemRaw[];
  total?: number;
  page?: number;
  page_size?: number;
};
export type CustomizationProjectItineraryListRaw = Schemas["CustomizationProjectItineraryList"];
export type CustomizationOptionsRaw = Schemas["CustomizationOptions"];
export type CustomizationProjectEditOptionsRaw = {
  college_candidates?: unknown[];
  customer_candidates?: unknown[];
  contact_candidates?: unknown[];
  major_candidates?: unknown[];
  province_names?: unknown;
  point_names?: unknown;
  stay_area_names?: unknown;
  dispatcher_candidates?: unknown[];
};
export type CustomizationProjectPointNameOptionsRaw = {
  point_names?: unknown;
};
export type CustomizationReferenceDemandListRaw = {
  items?: unknown[];
};
export type CustomizationRouteLinkRecognitionResultRaw = {
  recognized?: unknown;
  recognition_type?: unknown;
  article_id?: unknown;
  route?: unknown;
  trace_summary?: unknown;
};
export type CustomizationItineraryGenerationValidationRaw = {
  valid?: unknown;
  reasons?: unknown;
};
export type CustomizationDispatcherNotificationRaw = {
  sent_count?: unknown;
  failed_count?: unknown;
  skipped_count?: unknown;
  failure_messages?: unknown;
};
export type CustomizationCurrentUserTraceRoleRaw = {
  default_role?: unknown;
};
export type CreateCustomizationProjectWithTraceRaw = {
  project?: unknown;
  trace?: unknown;
};
export type CustomizationOpportunityContextRaw = {
  opportunity_id?: unknown;
  opportunity_code?: unknown;
  initial_trace_content?: unknown;
  org_id?: unknown;
  org_name?: unknown;
  customer_id?: unknown;
  customer_name?: unknown;
  contact_id?: unknown;
  contact_name?: unknown;
  linked_projects?: unknown;
};

type OpportunityCustomizationProjectListRaw =
  components["schemas"]["CustomizationOpportunityLinkedProjectList"];
type OpportunityCustomizationProjectItemRaw =
  components["schemas"]["CustomizationOpportunityLinkedProjectItem"];

/** 映射商机关联定制项目摘要。 */
function mapOpportunityCustomizationProjectItem(
  raw: OpportunityCustomizationProjectItemRaw,
): OpportunityCustomizationProjectItem {
  return {
    projectId: raw.project_id,
    projectCode: raw.project_code ?? undefined,
    projectName: raw.project_name,
    selectionStatus: raw.selection_status,
    currentDemandId: raw.current_demand_id ?? undefined,
    currentDemandVersionNumber: raw.current_demand_version_number ?? undefined,
    effectiveDemandId: raw.effective_demand_id ?? undefined,
    effectiveItineraryId: raw.effective_itinerary_id ?? undefined,
    effectiveSource: raw.effective_source ?? undefined,
    sourceTraceSummary: raw.source_trace_summary ?? undefined,
    demandSummary: raw.demand_summary ?? undefined,
    copyable: raw.copyable,
    traceCount: raw.trace_count ?? 0,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

/** 映射商机关联定制项目列表。 */
function mapOpportunityCustomizationProjectListItems(
  raw: OpportunityCustomizationProjectListRaw,
): OpportunityCustomizationProjectItem[] {
  return (raw.items ?? []).map(mapOpportunityCustomizationProjectItem);
}

/** 映射当前登录人在需求定制中的默认追溯角色。 */
export function mapCustomizationCurrentUserTraceRole(
  raw: CustomizationCurrentUserTraceRoleRaw,
): CustomizationCurrentUserTraceRole {
  return {
    defaultRole: raw.default_role === "dispatcher" ? "dispatcher" : "sales",
  };
}

/** 映射新增追溯记录请求。 */
export function mapCustomizationTraceCreatePayload(payload: CustomizationTraceCreate) {
  return omitUndefined({
    role: payload.role,
    source_type: payload.sourceType,
    content: payload.content.trim(),
    summary: payload.summary?.trim() || undefined,
    is_important: payload.isImportant,
    opportunity_id: payload.opportunityId,
  });
}

/** 映射从商机新增定制的预填上下文。 */
export function mapCustomizationOpportunityContext(
  raw: CustomizationOpportunityContextRaw,
): CustomizationOpportunityContext {
  const opportunityId = toOptionalNumber(raw.opportunity_id);
  const opportunityCode = toOptionalString(raw.opportunity_code);
  const initialTraceContent = toOptionalString(raw.initial_trace_content);
  if (!opportunityId || !opportunityCode || !initialTraceContent) {
    throw new Error("商机定制上下文接口返回了无效数据");
  }
  const linkedProjectsRaw =
    raw.linked_projects && typeof raw.linked_projects === "object"
      ? (raw.linked_projects as OpportunityCustomizationProjectListRaw)
      : { items: [] };
  return {
    opportunityId,
    opportunityCode,
    initialTraceContent,
    orgId: toOptionalNumber(raw.org_id),
    orgName: toOptionalString(raw.org_name),
    customerId: toOptionalNumber(raw.customer_id),
    customerName: toOptionalString(raw.customer_name),
    contactId: toOptionalNumber(raw.contact_id),
    contactName: toOptionalString(raw.contact_name),
    linkedProjects: mapOpportunityCustomizationProjectListItems(linkedProjectsRaw),
  };
}

/** 映射生成需求请求。 */
export function mapCreateCustomizationDemandPayload(payload: CreateCustomizationDemandPayload) {
  return omitUndefined({
    action: payload.action,
    selected_trace_ids: payload.selectedTraceIds,
    template_demand_id: payload.templateDemandId,
    base_demand_id: payload.baseDemandId,
    opportunity_id: payload.opportunityId,
    copy_source: payload.copySource,
    manual_patch: payload.manualPatch
      ? mapCustomizationProjectUpdatePayload(payload.manualPatch)
      : undefined,
  });
}

/** 映射商机归属决策请求。 */
export function mapCustomizationOpportunityBindingDecision(
  payload: CustomizationOpportunityBindingDecision,
): CustomizationOpportunityBindingDecisionRaw {
  return omitUndefined({
    decision: payload.decision,
    opportunity_id: payload.opportunityId,
    demand_id: payload.demandId,
    expected_project_updated_at: payload.expectedProjectUpdatedAt,
    decision_note: payload.decisionNote?.trim() || undefined,
    idempotency_key: payload.idempotencyKey?.trim() || undefined,
  }) as CustomizationOpportunityBindingDecisionRaw;
}

/** 映射通知计调请求。 */
export function mapCustomizationDispatcherNotificationPayload(
  payload: CustomizationDispatcherNotificationPayload = {},
) {
  return omitUndefined({
    demand_id: payload.demandId,
    source: payload.source,
    action_text: payload.actionText?.trim() || undefined,
  });
}

/** 映射旧系统外部导入请求。 */
export function mapLegacyCustomizeRouteImportPayload(payload: LegacyCustomizeRouteImportPayload) {
  return omitUndefined({
    legacy_batch_no: payload.legacyBatchNo.trim(),
    legacy_jidiao_no: payload.legacyJiDiaoNo.trim(),
  });
}

/** 映射项目字段保存请求。 */
export function mapCustomizationProjectUpdatePayload(payload: CustomizationProjectUpdatePayload) {
  return omitUndefined({
    customer_type: payload.customerType,
    trip_type: payload.tripType,
    sign_type: payload.signType,
    deal_progress: payload.dealProgress,
    dispatcher_user_id: payload.dispatcherUserId,
    operating_company: payload.operatingCompany,
    primary_school_id: payload.primarySchoolId,
    primary_school_name: payload.primarySchoolName,
    customer_id: payload.customerId,
    customer_name: payload.customerName,
    contact_id: payload.contactId,
    contact_name: payload.contactName,
    major_names: payload.majorNames,
    province_names: payload.provinces,
    point_names: payload.pointNames,
    stay_area_names: payload.stayAreaNames,
    destination_summary: payload.destinationSummary,
    duration_days: payload.durationDays,
    departure_start_date: payload.departureStartDate,
    student_count: payload.studentCount,
    teacher_count: payload.teacherCount,
    total_count: payload.totalCount,
    budget_per_person: payload.budgetPerPerson,
    teaching_requirements: payload.teachingRequirements,
    special_requirements: payload.specialRequirements,
    school_pickup_requirement: payload.schoolPickupRequirement,
    invoice_requirement: payload.invoiceRequirement,
    teacher_meal_requirement: payload.teacherMealRequirement,
    teacher_ticket_requirement: payload.teacherTicketRequirement,
    teacher_transport_requirement: payload.teacherTransportRequirement,
    teacher_accommodation_requirement: payload.teacherAccommodationRequirement,
    remark: payload.remark,
    round_trip_transport_note: payload.roundTripTransportNote,
    local_transport_note: payload.localTransportNote,
    accommodation_note: payload.accommodationNote,
    guide_count: payload.guideCount,
    guide_note: payload.guideNote,
    include_tickets: payload.includeTickets,
    ticket_note: payload.ticketNote,
    include_meals: payload.includeMeals,
    meal_standards: payload.mealStandards,
    meal_note: payload.mealNote,
    company_visit_count: payload.companyVisitCount,
    company_visit_note: payload.companyVisitNote,
    intangible_heritage_count: payload.intangibleHeritageCount,
    intangible_heritage_note: payload.intangibleHeritageNote,
  });
}

/** 映射项目编辑候选查询参数。 */
export function mapCustomizationProjectEditOptionsParams(
  params: CustomizationProjectEditOptionsParams,
) {
  return omitUndefined({
    keyword: params.keyword?.trim() || undefined,
    primary_school_id: params.primarySchoolId,
    customer_id: params.customerId,
  });
}

/** 映射项目考察点候选查询参数。 */
export function mapCustomizationProjectPointNameOptionsParams(
  params: CustomizationProjectPointNameOptionsParams,
) {
  const stayAreaNames = normalizeStringArray(params.stayAreaNames)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item, index, all) => all.indexOf(item) === index);
  return omitUndefined({
    keyword: params.keyword?.trim() || undefined,
    stay_area_names: stayAreaNames.length ? stayAreaNames : undefined,
  });
}

/** 映射项目编辑候选选项。 */
export function mapCustomizationProjectEditOptions(
  raw: CustomizationProjectEditOptionsRaw,
): CustomizationProjectEditOptions {
  return {
    collegeCandidates: normalizeRecordArray(raw.college_candidates).map(mapCollegeCandidate),
    customerCandidates: normalizeRecordArray(raw.customer_candidates).map(mapCustomerCandidate),
    contactCandidates: normalizeRecordArray(raw.contact_candidates).map(mapContactCandidate),
    majorCandidates: normalizeRecordArray(raw.major_candidates).map(mapMajorCandidate),
    provinceNames: normalizeStringArray(raw.province_names),
    pointNames: normalizeStringArray(raw.point_names),
    stayAreaNames: normalizeStringArray(raw.stay_area_names),
    dispatcherCandidates: normalizeDispatcherCandidates(raw.dispatcher_candidates),
  };
}

/** 映射项目考察点候选选项。 */
export function mapCustomizationProjectPointNameOptions(
  raw: CustomizationProjectPointNameOptionsRaw,
): CustomizationProjectPointNameOptions {
  return {
    pointNames: normalizeStringArray(raw.point_names),
  };
}

/** 映射定制项目列表查询参数。 */
export function mapCustomizationProjectListParams(params: CustomizationProjectListParams) {
  return omitUndefined({
    page: params.page,
    page_size: params.pageSize,
    scope: params.scope,
    opportunity_id: params.opportunityId,
    keyword: params.keyword?.trim() || undefined,
    organization_name: params.organizationName?.trim() || undefined,
    destination: params.destination?.trim() || undefined,
    deal_progress: params.dealProgress,
    opportunity_group_source: params.opportunityGroupSource,
    trip_type: params.tripType,
    sign_type: params.signType,
    dispatcher_user_id: params.dispatcherUserId,
    operating_company: params.operatingCompany,
    has_itinerary: params.hasItinerary,
    has_order: params.hasOrder,
    has_opportunity: params.hasOpportunity,
    sort_by: params.sortBy,
    sort_order: params.sortOrder,
  });
}

/** 映射工作台响应。 */
export function mapCustomizationWorkbench(raw: CustomizationWorkbenchRaw): CustomizationWorkbench {
  const rawWorkbench = raw as unknown as Record<string, unknown>;
  return {
    project: mapCustomizationProjectSnapshot(raw.project as Record<string, unknown>),
    currentDemand: raw.current_demand ? mapCustomizationDemand(raw.current_demand) : null,
    effectiveDemandId: toOptionalString(rawWorkbench.effective_demand_id),
    effectiveItineraryId: toOptionalString(rawWorkbench.effective_itinerary_id),
    effectiveSource:
      rawWorkbench.effective_source === "contract_handover"
        ? "contract_handover"
        : "latest_demand",
    demandVersions: (raw.demand_versions ?? []).map(mapCustomizationDemandVersionOption),
    traces: (raw.traces ?? []).map(mapCustomizationTraceItem),
    projectFieldOptions: mapCustomizationProjectFieldOptions(rawWorkbench.project_field_options),
    demandFieldOptions: mapCustomizationDemandFieldOptions(rawWorkbench.demand_field_options),
    opportunityBinding: mapCustomizationOpportunityBinding(
      raw.opportunity_binding ?? buildIncompleteOpportunityBindingRaw(raw),
    ),
  };
}

/** 映射生成需求响应。 */
export function mapCreateDemandResult(raw: CreateDemandResultRaw): CreateCustomizationDemandResult {
  return {
    project: mapCustomizationProjectSnapshot(raw.project as Record<string, unknown>),
    demand: mapCustomizationDemand(raw.demand),
    selectedTraces: (raw.selected_traces ?? []).map(mapCustomizationTraceItem),
    traceUsage: normalizeTraceUsage(raw.trace_usage),
    conflicts: raw.conflicts ?? [],
    needsHumanReviewReasons: raw.needs_human_review_reasons ?? [],
    opportunityBinding: mapCustomizationOpportunityBinding(
      raw.opportunity_binding ?? {
        project_id: raw.project.id,
        demand_id: raw.demand.id,
        status: "incomplete",
        required_complete: false,
        restricted_candidate_count: 0,
      },
    ),
    dispatcherNotification: raw.dispatcher_notification
      ? mapCustomizationDispatcherNotification(raw.dispatcher_notification)
      : null,
  };
}

/** 将后端商机归属 DTO 映射为领域对象。 */
export function mapCustomizationOpportunityBinding(
  raw: CustomizationOpportunityBindingRaw,
): CustomizationOpportunityBinding {
  return {
    projectId: raw.project_id,
    demandId: raw.demand_id ?? undefined,
    status: raw.status,
    requiredComplete: raw.required_complete,
    currentOpportunity: raw.current_opportunity
      ? {
          opportunityId: raw.current_opportunity.opportunity_id,
          opportunityCode: raw.current_opportunity.opportunity_code,
          opportunityName: raw.current_opportunity.opportunity_name,
          status: raw.current_opportunity.status,
        }
      : null,
    candidates: (raw.candidates ?? []).map((candidate) => ({
      opportunityId: candidate.opportunity_id,
      opportunityCode: candidate.opportunity_code,
      opportunityName: candidate.opportunity_name,
      status: candidate.status,
      stage: candidate.stage,
      ownerUserName: candidate.owner_user_name ?? undefined,
      matchScore: candidate.match_score,
      matchedDimensions: candidate.matched_dimensions ?? [],
      differentDimensions: candidate.different_dimensions ?? [],
      matchSummary: candidate.match_summary,
      selectable: candidate.selectable,
      unselectableReason: candidate.unselectable_reason ?? undefined,
      linkedCustomizationCount: candidate.linked_customization_count,
    })),
    restrictedCandidateCount: raw.restricted_candidate_count,
    reasonCode: raw.reason_code ?? undefined,
    projectUpdatedAt: raw.project_updated_at ?? undefined,
  };
}

/** 为兼容旧接口响应构造不完整归属 DTO。 */
function buildIncompleteOpportunityBindingRaw(
  raw: CustomizationWorkbenchRaw,
): CustomizationOpportunityBindingRaw {
  return {
    project_id: raw.project.id,
    demand_id: raw.current_demand?.id,
    status: "incomplete",
    required_complete: false,
    restricted_candidate_count: 0,
  };
}

/** 映射临时草稿首追溯建项响应。 */
export function mapCreateCustomizationProjectWithTraceResult(
  raw: CreateCustomizationProjectWithTraceRaw,
): CreateCustomizationProjectWithTraceResult {
  if (!raw.project || !raw.trace) {
    throw new Error("首条追溯建项接口返回了无效数据");
  }
  return {
    project: mapCustomizationProjectSnapshot(raw.project as Record<string, unknown>),
    trace: mapCustomizationTraceItem(raw.trace as CustomizationTraceItemRaw),
  };
}

/** 映射定制项目列表响应。 */
export function mapCustomizationProjectListPage(
  raw: CustomizationProjectListPageRaw,
): CustomizationProjectListPage {
  const items = Array.isArray(raw.items)
    ? raw.items.map((item) => mapCustomizationProjectListItem(item as Record<string, unknown>))
    : [];
  return {
    items,
    total: toOptionalNumber(raw.total) ?? 0,
    page: toOptionalNumber(raw.page) ?? 1,
    pageSize: toOptionalNumber(raw.page_size) ?? 20,
    viewerRole: raw.viewer_role,
    sortBy: raw.sort_by,
    sortOrder: raw.sort_order,
  };
}

/** 映射项目全部需求版本。 */
export function mapCustomizationProjectDemandVersionList(
  raw: CustomizationProjectDemandVersionListRaw,
): CustomizationProjectDemandVersionList {
  return {
    items: (raw.items ?? []).map(mapCustomizationDemandVersionOption),
  };
}

/** 映射未做方案版本查询参数。 */
export function mapCustomizationDemandVersionWithoutPlanParams(
  params: CustomizationDemandVersionWithoutPlanParams,
) {
  return {
    page: params.page,
    page_size: params.pageSize,
    scope: params.scope === "all" ? "all" : "mine",
    project_code: params.projectCode?.trim() || undefined,
  };
}

/** 映射未做方案版本分页。 */
export function mapCustomizationDemandVersionWithoutPlanPage(
  raw: CustomizationDemandVersionWithoutPlanPageRaw,
): CustomizationDemandVersionWithoutPlanPage {
  return {
    items: (raw.items ?? []).map(mapCustomizationDemandVersionWithoutPlanItem),
    total: Number(raw.total ?? 0),
    page: Number(raw.page ?? 1),
    pageSize: Number(raw.page_size ?? 20),
  };
}

/** 映射未做方案需求版本列表单项. */
function mapCustomizationDemandVersionWithoutPlanItem(
  raw: CustomizationDemandVersionWithoutPlanItemRaw,
): CustomizationDemandVersionWithoutPlanItem {
  return {
    id: String(raw.id ?? ""),
    projectId: String(raw.project_id ?? ""),
    projectCode: String(raw.project_code ?? ""),
    projectName: String(raw.project_name ?? ""),
    legacyId: toOptionalString(raw.legacy_id),
    operatingCompany: toOptionalString(raw.operating_company),
    salesUserName: toOptionalString(raw.sales_user_name),
    dispatcherUserName: toOptionalString(raw.dispatcher_user_name),
    customerType: toOptionalString(raw.customer_type),
    versionNumber: String(raw.version_number ?? ""),
    versionSeq: Number(raw.version_seq ?? 0),
    createdAt: String(raw.created_at ?? ""),
    orderVersionKey: toOptionalString(raw.order_version_key),
    departureStartDate: toOptionalString(raw.departure_start_date),
    studentCount: toOptionalNumber(raw.student_count),
    teacherCount: toOptionalNumber(raw.teacher_count),
    provinceNames: raw.province_names ?? [],
    stayAreaNames: raw.stay_area_names ?? [],
    pointNames: raw.point_names ?? [],
    hasItinerary: Boolean(raw.has_itinerary),
    itineraryCount: Number(raw.itinerary_count ?? 0),
    finalizedBudgetCount: Number(raw.finalized_budget_count ?? 0),
  };
}

/** 映射项目全部行程。 */
export function mapCustomizationProjectItineraryList(
  raw: CustomizationProjectItineraryListRaw,
): CustomizationProjectItineraryList {
  return {
    projectName: raw.project_name,
    projectNumber: raw.project_number,
    items: (raw.items ?? []).map((item) => ({
      id: String(item.id ?? ""),
      sourceDemandId: toOptionalString(item.source_demand_id),
      title: toOptionalString(item.title),
      demandVersionNumber: toOptionalString(item.demand_version_number),
      startDate: toOptionalString(item.start_date),
      endDate: toOptionalString(item.end_date),
      summary: toOptionalString(item.summary),
      proposalArticleId: toOptionalString(item.proposal_article_id),
      pricedArticleId: toOptionalString(item.priced_article_id),
      finalizedBudgetCount: toOptionalNumber(item.finalized_budget_count) ?? 0,
      finalizedQuoteCount: toOptionalNumber(item.finalized_quote_count) ?? 0,
      isFormed: item.is_formed,
      latestFinalizedQuoteTotalAmount: toOptionalString(
        item.latest_finalized_quote_total_amount,
      ),
      linkedFinalizedBudgetTotalAmount: toOptionalString(
        item.linked_finalized_budget_total_amount,
      ),
      createdAt: String(item.created_at ?? ""),
      updatedAt: String(item.updated_at ?? ""),
    })),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
  };
}

/** 映射需求定制枚举元数据响应。 */
export function mapCustomizationOptions(raw: CustomizationOptionsRaw): CustomizationOptions {
  const payload = raw as unknown as Record<string, unknown>;
  return {
    projectFieldOptions: mapCustomizationProjectFieldOptions(payload.project_field_options),
    demandFieldOptions: mapCustomizationDemandFieldOptions(payload.demand_field_options),
  };
}

/** 映射参考定制需求预览响应。 */
export function mapCustomizationReferenceDemandList(
  raw: CustomizationReferenceDemandListRaw,
): CustomizationReferenceDemandList {
  const payload = raw as unknown as Record<string, unknown>;
  return {
    items: Array.isArray(payload.items)
      ? payload.items
          .map((item) => mapCustomizationReferenceDemandCandidate(item))
          .filter((item): item is CustomizationReferenceDemandCandidate => Boolean(item))
      : [],
  };
}

/** 映射旧系统行程直链识别预览响应。 */
export function mapCustomizationRouteLinkRecognitionResult(
  raw: CustomizationRouteLinkRecognitionResultRaw,
): CustomizationRouteLinkRecognitionResult {
  const payload = raw as Record<string, unknown>;
  const route = mapCustomizationLegacyRoutePreview(payload.route);
  return {
    recognized: payload.recognized === true,
    recognitionType: "legacy_route_link",
    articleId: toOptionalString(payload.article_id),
    route,
    traceSummary: toOptionalString(payload.trace_summary),
  };
}

/** 映射生成行程前置校验结果。 */
export function mapCustomizationItineraryGenerationValidation(
  raw: CustomizationItineraryGenerationValidationRaw,
): CustomizationItineraryGenerationValidationResult {
  return {
    valid: raw.valid === true,
    reasons: normalizeStringArray(raw.reasons),
  };
}

/** 映射参考定制需求候选。 */
/** 映射通知计调结果。 */
export function mapCustomizationDispatcherNotification(
  raw: CustomizationDispatcherNotificationRaw,
): CustomizationDispatcherNotificationResult {
  return {
    sentCount: toOptionalNumber(raw.sent_count) ?? 0,
    failedCount: toOptionalNumber(raw.failed_count) ?? 0,
    skippedCount: toOptionalNumber(raw.skipped_count) ?? 0,
    failureMessages: normalizeStringArray(raw.failure_messages),
  };
}

/** 映射参考定制需求候选。 */
function mapCustomizationReferenceDemandCandidate(
  value: unknown,
): CustomizationReferenceDemandCandidate | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const payload = value as Record<string, unknown>;
  const demandId = toOptionalString(payload.demand_id);
  if (!demandId) {
    return null;
  }
  return {
    referenceKey: toOptionalString(payload.reference_key) ?? "",
    projectId: toOptionalString(payload.project_id) ?? "",
    demandId,
    projectCode: toOptionalString(payload.project_code) ?? "",
    legacyId: toOptionalString(payload.legacy_id),
    versionNumber: toOptionalString(payload.version_number) ?? "",
    versionSeq: toOptionalNumber(payload.version_seq) ?? 0,
    title: toOptionalString(payload.title) ?? "",
    highlights: normalizeStringArray(payload.highlights),
  };
}

/** 映射旧系统行程预览。 */
function mapCustomizationLegacyRoutePreview(value: unknown): CustomizationLegacyRoutePreview | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const payload = value as Record<string, unknown>;
  const articleId = toOptionalString(payload.article_id);
  const title = toOptionalString(payload.title);
  if (!articleId || !title) {
    return null;
  }
  return {
    articleId,
    title,
    startDate: toOptionalString(payload.start_date),
    endDate: toOptionalString(payload.end_date),
    summary: toOptionalString(payload.summary),
    teamId: toOptionalString(payload.team_id),
    legacyBatchNo: toOptionalString(payload.legacy_batch_no),
    legacyJidiaoNo: toOptionalString(payload.legacy_jidiao_no),
    dispatcherUserId: toOptionalString(payload.dispatcher_user_id),
    dispatcherUserName: toOptionalString(payload.dispatcher_user_name),
    primarySchoolName: toOptionalString(payload.primary_school_name),
    customerName: toOptionalString(payload.customer_name),
    contactName: toOptionalString(payload.contact_name),
    customerType: toOptionalString(payload.customer_type),
    tripType: toOptionalString(payload.trip_type),
    signType: toOptionalString(payload.sign_type),
    operatingCompany: toOptionalString(payload.operating_company),
    studentCount: toOptionalNumber(payload.student_count),
    teacherCount: toOptionalNumber(payload.teacher_count),
    totalCount: toOptionalNumber(payload.total_count),
    durationDays: toOptionalNumber(payload.duration_days),
    schoolPickupRequirement: toOptionalString(payload.school_pickup_requirement),
    invoiceRequirement: toOptionalString(payload.invoice_requirement),
    teacherMealRequirement: toOptionalString(payload.teacher_meal_requirement),
    teacherTicketRequirement: toOptionalString(payload.teacher_ticket_requirement),
    teacherTransportRequirement: toOptionalString(payload.teacher_transport_requirement),
    teacherAccommodationRequirement: toOptionalString(payload.teacher_accommodation_requirement),
    remark: toOptionalString(payload.remark),
    specialRequirements: toOptionalString(payload.special_requirements),
    pointNames: normalizeRoutePreviewNameArray(payload.point_names),
    stayAreaNames: normalizeRoutePreviewNameArray(payload.stay_area_names),
  };
}

/** 映射定制项目列表项。 */
export function mapCustomizationProjectListItem(
  raw: Record<string, unknown>,
): CustomizationProjectListItem {
  return {
    id: String(raw.id ?? ""),
    legacyId: toOptionalString(raw.legacy_id),
    projectCode: String(raw.project_code ?? ""),
    name: String(raw.name ?? ""),
    status: toOptionalString(raw.status),
    stage: toOptionalString(raw.stage),
    organizationNameSnapshot: toOptionalString(raw.organization_name_snapshot),
    customerName: toOptionalString(raw.customer_name),
    contactName: toOptionalString(raw.contact_name),
    customerType: toOptionalString(raw.customer_type),
    dealProgress: toOptionalString(raw.deal_progress) ?? "50%",
    opportunityGroupCode: toOptionalString(raw.opportunity_group_code),
    opportunityGroupSource: toOptionalString(raw.opportunity_group_source),
    signType: toOptionalString(raw.sign_type),
    tripType: toOptionalString(raw.trip_type),
    salesUserName: toOptionalString(raw.sales_user_name),
    dispatcherUserId: toOptionalString(raw.dispatcher_user_id),
    dispatcherUserName: toOptionalString(raw.dispatcher_user_name),
    dispatcherUserPhone: toOptionalString(raw.dispatcher_user_phone),
    operatingCompany: toOptionalString(raw.operating_company),
    versionNumber: toOptionalString(raw.version_number),
    versionSeq: toOptionalNumber(raw.version_seq),
    effectiveDemandId: toOptionalString(raw.effective_demand_id),
    projectCreatedAt: toOptionalString(raw.project_created_at),
    versionUpdatedAt: toOptionalString(raw.version_updated_at),
    versionUpdatedByName: toOptionalString(raw.version_updated_by_name),
    durationDays: toOptionalNumber(raw.duration_days),
    departureStartDate: toOptionalString(raw.departure_start_date),
    studentCount: toOptionalNumber(raw.student_count),
    teacherCount: toOptionalNumber(raw.teacher_count),
    totalCount: toOptionalNumber(raw.total_count),
    schoolPickupRequirement: toOptionalString(raw.school_pickup_requirement),
    invoiceRequirement: toOptionalString(raw.invoice_requirement),
    teacherMealRequirement: toOptionalString(raw.teacher_meal_requirement),
    teacherTicketRequirement: toOptionalString(raw.teacher_ticket_requirement),
    teacherTransportRequirement: toOptionalString(raw.teacher_transport_requirement),
    teacherAccommodationRequirement: toOptionalString(raw.teacher_accommodation_requirement),
    companyVisitCount: toOptionalNumber(raw.company_visit_count),
    companyVisitNote: toOptionalString(raw.company_visit_note),
    guideCount: toOptionalNumber(raw.guide_count),
    guideNote: toOptionalString(raw.guide_note),
    includeMeals: toOptionalBoolean(raw.include_meals),
    mealStandards: normalizeStringArray(raw.meal_standards),
    mealNote: toOptionalString(raw.meal_note),
    includeTickets: toOptionalBoolean(raw.include_tickets),
    ticketNote: toOptionalString(raw.ticket_note),
    intangibleHeritageCount: toOptionalNumber(raw.intangible_heritage_count),
    intangibleHeritageNote: toOptionalString(raw.intangible_heritage_note),
    localTransportNote: toOptionalString(raw.local_transport_note),
    roundTripTransportNote: toOptionalString(raw.round_trip_transport_note),
    destinationSummary: toOptionalString(raw.destination_summary),
    demandVersionCount: toOptionalNumber(raw.demand_version_count) ?? 0,
    hasItinerary: toOptionalBoolean(raw.has_itinerary),
    hasOrder: toOptionalBoolean(raw.has_order) ?? false,
    effectiveStateConflict: toOptionalString(raw.effective_state_conflict),
    isFormed: toOptionalBoolean(raw.is_formed) ?? false,
    itineraryCount: toOptionalNumber(raw.itinerary_count) ?? 0,
    latestItineraryUpdatedAt: toOptionalString(raw.latest_itinerary_updated_at),
    latestItineraryId: toOptionalString(raw.latest_itinerary_id),
    itineraryArticleId: toOptionalString(raw.itinerary_article_id),
    itineraryModifiedByName: toOptionalString(raw.itinerary_modified_by_name),
  };
}

/** 映射追溯记录响应。 */
export function mapCustomizationTraceItem(raw: CustomizationTraceItemRaw): CustomizationTraceItem {
  const rawTrace = raw as unknown as Record<string, unknown>;
  return {
    id: raw.id,
    role: raw.role,
    sourceType: raw.source_type,
    authorName: toOptionalString(rawTrace.author_name),
    content: raw.content,
    summary: raw.summary ?? "",
    isImportant: raw.is_important,
    isSelectable: rawTrace.is_selectable !== false,
    createdAt: raw.created_at,
  };
}

/** 映射定制需求版本响应。 */
export function mapCustomizationDemand(
  raw: Schemas["CustomizationDemandDraft"],
): CustomizationDemandDraft {
  const rawDemand = raw as unknown as Record<string, unknown>;
  const summary = normalizeSummarySection(rawDemand.summary);
  const customerRequirements = normalizeCustomerRequirementsSection(rawDemand.customer_requirements);
  const teacherRequirements = normalizeTeacherRequirementsSection(rawDemand.teacher_requirements);
  const relations = normalizeRelationsSection(rawDemand.relations);
  const displayGroups = normalizeDisplayGroups(rawDemand.field_groups);

  return {
    id: raw.id ?? undefined,
    projectId: raw.project_id ?? undefined,
    versionNumber: raw.version_number ?? undefined,
    versionSeq: raw.version_seq ?? undefined,
    previousDemandId: toOptionalString(rawDemand.previous_demand_id),
    copiedFromDemandId: toOptionalString(rawDemand.copied_from_demand_id),
    generationMode: toOptionalString(rawDemand.generation_mode),
    sourceTraceSummary: summary?.sourceTraceSummary ?? raw.source_trace_summary ?? undefined,
    demandSummary: summary?.demandSummary ?? raw.demand_summary,
    destinationSummary: summary?.destinationSummary ?? raw.destination_summary ?? undefined,
    durationDays: summary?.durationDays ?? raw.duration_days ?? undefined,
    travelTime: summary?.travelTime ?? normalizeRecord(raw.travel_time),
    departureStartDate:
      summary?.departureStartDate ??
      (raw.departure_start_date === null || raw.departure_start_date === undefined
        ? undefined
        : String(raw.departure_start_date)),
    totalCount: summary?.totalCount ?? raw.total_count ?? undefined,
    studentCount: summary?.studentCount ?? raw.student_count ?? undefined,
    teacherCount: summary?.teacherCount ?? raw.teacher_count ?? undefined,
    budgetPerPerson: summary?.budgetPerPerson ?? raw.budget_per_person ?? undefined,
    summary,
    customerRequirements,
    teacherRequirements,
    teachingRequirements:
      customerRequirements?.teachingRequirements ?? raw.teaching_requirements ?? undefined,
    specialRequirements:
      customerRequirements?.specialRequirements ?? raw.special_requirements ?? undefined,
    schoolPickupRequirement:
      customerRequirements?.schoolPickupRequirement ?? raw.school_pickup_requirement ?? undefined,
    invoiceRequirement:
      customerRequirements?.invoiceRequirement ?? raw.invoice_requirement ?? undefined,
    teacherMealRequirement:
      teacherRequirements?.teacherMealRequirement ?? raw.teacher_meal_requirement ?? undefined,
    teacherTicketRequirement:
      teacherRequirements?.teacherTicketRequirement ?? raw.teacher_ticket_requirement ?? undefined,
    teacherTransportRequirement:
      teacherRequirements?.teacherTransportRequirement ?? raw.teacher_transport_requirement ?? undefined,
    teacherAccommodationRequirement:
      teacherRequirements?.teacherAccommodationRequirement ??
      raw.teacher_accommodation_requirement ??
      undefined,
    remark: customerRequirements?.remark ?? raw.remark ?? undefined,
    serviceStandard: mapCustomizationServiceStandard(rawDemand.service_standard),
    relations,
    displayGroups,
    traceUsage: normalizeTraceUsage(rawDemand.trace_usage),
    changedFields: normalizeChangedFields(raw.changed_fields),
    needsHumanReviewReasons: normalizeStringArray(rawDemand.needs_human_review_reasons),
    createdAt: raw.created_at ?? undefined,
    updatedAt: raw.updated_at ?? undefined,
    project: undefined,
  };
}

/** 映射需求版本下拉选项。 */
function mapCustomizationDemandVersionOption(
  raw: Schemas["CustomizationDemandVersionOption"],
): CustomizationDemandVersionOption {
  const payload = raw as unknown as Record<string, unknown>;
  return {
    id: raw.id,
    versionNumber: raw.version_number,
    versionSeq: raw.version_seq,
    createdAt: raw.created_at,
    isCurrent: raw.is_current,
    orderVersionKey: toOptionalString(payload.order_version_key),
    hasItinerary: toOptionalBoolean(payload.has_itinerary) ?? false,
    itineraryCount: toOptionalNumber(payload.itinerary_count) ?? 0,
    itineraryTitle: toOptionalString(payload.itinerary_title),
    latestItineraryId: toOptionalString(payload.latest_itinerary_id),
    itineraryArticleId: toOptionalString(payload.itinerary_article_id),
    itineraryModifiedByName: toOptionalString(payload.itinerary_modified_by_name),
    needsHumanReviewReasons: normalizeStringArray(payload.needs_human_review_reasons),
    updatedAt: toOptionalString(payload.updated_at),
    departureStartDate: toOptionalString(payload.departure_start_date),
    studentCount: toOptionalNumber(payload.student_count),
    teacherCount: toOptionalNumber(payload.teacher_count),
    provinceNames: normalizeStringArray(payload.province_names),
    stayAreaNames: normalizeStringArray(payload.stay_area_names),
    pointNames: normalizeStringArray(payload.point_names),
    finalizedBudgetCount: toOptionalNumber(payload.finalized_budget_count) ?? 0,
    latestFinalizedBudgetOrderId: toOptionalString(
      payload.latest_finalized_budget_order_id,
    ),
    latestFinalizedQuoteTotalAmount: toOptionalString(
      payload.latest_finalized_quote_total_amount,
    ),
  };
}

/** 将未知 JSON 值规整为普通对象。 */
function normalizeRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

/** 映射项目快照。 */
export function mapCustomizationProjectSnapshot(
  raw: Record<string, unknown>,
): CustomizationWorkbench["project"] {
  return {
    id: String(raw.id ?? ""),
    legacyId: toOptionalString(raw.legacy_id),
    projectCode: String(raw.project_code ?? ""),
    name: String(raw.name ?? ""),
    currentDemandId: toOptionalString(raw.current_demand_id),
    status: toOptionalString(raw.status),
    stage: toOptionalString(raw.stage),
    customerId: toOptionalString(raw.customer_id),
    customerName: toOptionalString(raw.customer_name),
    contactId: toOptionalString(raw.contact_id),
    contactName: toOptionalString(raw.contact_name),
    customerType: toOptionalString(raw.customer_type),
    tripType: toOptionalString(raw.trip_type),
    signType: toOptionalString(raw.sign_type),
    quoteMode: toOptionalString(raw.quote_mode),
    dealProgress: toOptionalString(raw.deal_progress),
    salesUserId: toOptionalString(raw.sales_user_id),
    salesUserName: toOptionalString(raw.sales_user_name),
    dispatcherUserId: toOptionalString(raw.dispatcher_user_id),
    dispatcherUserName: toOptionalString(raw.dispatcher_user_name),
    dispatcherUserPhone: toOptionalString(raw.dispatcher_user_phone),
    operatingCompany: toOptionalString(raw.operating_company),
    primarySchoolId: toOptionalString(raw.primary_school_id),
    primarySchoolNameSnapshot: toOptionalString(raw.primary_school_name_snapshot),
    primarySchoolOrgType: toOptionalString(raw.primary_school_org_type),
  };
}

/** 去掉 undefined 查询参数。 */
function omitUndefined<T extends Record<string, unknown>>(payload: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

/** 映射项目字段候选选项。 */
function mapCustomizationProjectFieldOptions(
  raw: unknown,
): CustomizationProjectFieldOptions | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const payload = raw as Record<string, unknown>;
  return {
    customerTypeOptions: normalizeStringArray(payload.customer_type_options),
    tripTypeOptions: normalizeStringArray(payload.trip_type_options),
    signTypeOptions: normalizeStringArray(payload.sign_type_options),
    dealProgressOptions: normalizeStringArray(payload.deal_progress_options),
    operatingCompanyOptions: normalizeStringArray(payload.operating_company_options),
    dispatcherCandidates: normalizeDispatcherCandidates(payload.dispatcher_candidates),
  };
}

/** 映射需求字段候选选项。 */
function mapCustomizationDemandFieldOptions(
  raw: unknown,
): CustomizationDemandFieldOptions | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const payload = raw as Record<string, unknown>;
  return {
    schoolPickupRequirementOptions: normalizeStringArray(
      payload.school_pickup_requirement_options,
    ),
  };
}

/** 映射第一计调候选。 */
function normalizeDispatcherCandidates(value: unknown): CustomizationReferenceUserCandidate[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return null;
      }
      const payload = item as Record<string, unknown>;
      const userId = toOptionalString(payload.user_id);
      const displayName = toOptionalString(payload.display_name);
      if (!userId || !displayName) {
        return null;
      }
      return {
        userId,
        displayName,
        phone: toOptionalString(payload.phone),
      };
    })
    .filter((item): item is CustomizationReferenceUserCandidate => Boolean(item));
}

/** 映射第一计调选择弹窗候选。 */
export function mapDispatcherSelectionCandidates(value: unknown): CustomizationDispatcherSelectionCandidate[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return null;
      }
      const payload = item as Record<string, unknown>;
      const userId = toOptionalString(payload.user_id);
      const displayName = toOptionalString(payload.display_name);
      if (!userId || !displayName) {
        return null;
      }
      return {
        userId,
        displayName,
        phone: toOptionalString(payload.phone),
        avatarUrl: toOptionalString(payload.avatar_url),
        pendingOrderCount: Number(payload.pending_order_count ?? 0) || 0,
        followingOrderCount: Number(payload.following_order_count ?? 0) || 0,
        unfinishedGroupCount: Number(payload.unfinished_group_count ?? 0) || 0,
        finishedGroupCount: Number(payload.finished_group_count ?? 0) || 0,
      };
    })
    .filter((item): item is CustomizationDispatcherSelectionCandidate => Boolean(item));
}

/** 映射计调未接单/在跟单明细列表。 */
export function mapDispatcherSelectionOrderDetails(
  value: unknown,
): CustomizationDispatcherOrderDetailItem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return null;
      }
      const payload = item as Record<string, unknown>;
      const jidiaoNo = toOptionalString(payload.jidiao_no);
      if (!jidiaoNo) {
        return null;
      }
      return {
        jidiaoNo,
        businessOpportunityName: toOptionalString(payload.business_opportunity_name) ?? "",
        salesName: toOptionalString(payload.sales_name) ?? "",
        lastPriceTimeLabel: toOptionalString(payload.last_price_time_label) ?? "",
        startDateLabel: toOptionalString(payload.start_date_label) ?? "",
      };
    })
    .filter((item): item is CustomizationDispatcherOrderDetailItem => Boolean(item));
}

/** 映射当前登录计调「我的未接单」工作量响应。 */
export function mapMyDispatcherWorkload(value: unknown): CustomizationMyWorkloadPayload {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {
      detailType: "pending",
      pendingOrderCount: 0,
      followingOrderCount: 0,
      unfinishedGroupCount: 0,
      finishedGroupCount: 0,
      orderItems: [],
      groupItems: [],
    };
  }
  const payload = value as Record<string, unknown>;
  const detailTypeRaw = toOptionalString(payload.detail_type);
  const detailType: CustomizationMyWorkloadDetailType =
    detailTypeRaw === "following"
    || detailTypeRaw === "unfinished"
    || detailTypeRaw === "finished"
      ? detailTypeRaw
      : "pending";
  const groupItems = Array.isArray(payload.group_items)
    ? payload.group_items
        .map((item) => {
          if (!item || typeof item !== "object" || Array.isArray(item)) {
            return null;
          }
          const row = item as Record<string, unknown>;
          const teamId = toOptionalString(row.team_id);
          if (!teamId) {
            return null;
          }
          return {
            teamId,
            title: toOptionalString(row.title) ?? teamId,
            jidiaoBatchNo: toOptionalString(row.jidiao_batch_no) ?? "",
            startDateLabel: toOptionalString(row.start_date_label) ?? "",
            endDateLabel: toOptionalString(row.end_date_label) ?? "",
          };
        })
        .filter((item): item is CustomizationDispatcherGroupDetailItem => Boolean(item))
    : [];
  return {
    detailType,
    pendingOrderCount: Number(payload.pending_order_count ?? 0) || 0,
    followingOrderCount: Number(payload.following_order_count ?? 0) || 0,
    unfinishedGroupCount: Number(payload.unfinished_group_count ?? 0) || 0,
    finishedGroupCount: Number(payload.finished_group_count ?? 0) || 0,
    orderItems: mapDispatcherSelectionOrderDetails(payload.order_items),
    groupItems,
  };
}

/** 将未知数组规整为对象数组。 */
function normalizeRecordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === "object" && !Array.isArray(item),
  );
}

/** 映射高校/组织候选。 */
function mapCollegeCandidate(payload: Record<string, unknown>): CustomizationCollegeCandidate {
  return {
    id: String(payload.id ?? ""),
    name: String(payload.name ?? ""),
    orgType: toOptionalString(payload.org_type),
    province: toOptionalString(payload.province),
    city: toOptionalString(payload.city),
  };
}

/** 映射客户候选。 */
function mapCustomerCandidate(payload: Record<string, unknown>): CustomizationCustomerCandidate {
  return {
    id: String(payload.id ?? ""),
    name: String(payload.name ?? ""),
    anchorLevel: toOptionalString(payload.anchor_level),
    anchorOrgUnitName: toOptionalString(payload.anchor_org_unit_name),
    businessStatus: toOptionalString(payload.business_status),
  };
}

/** 映射联系人候选。 */
function mapContactCandidate(payload: Record<string, unknown>): CustomizationContactCandidate {
  return {
    id: String(payload.id ?? ""),
    name: String(payload.name ?? ""),
    mobile: toOptionalString(payload.mobile),
    positionName: toOptionalString(payload.position_name),
    mainOrgUnitName: toOptionalString(payload.main_org_unit_name),
    isKeyContact: Boolean(payload.is_key_contact),
    isCurrentCustomerRelated: Boolean(payload.is_current_customer_related),
    isPrimaryForCurrentCustomer: Boolean(payload.is_primary_for_current_customer),
  };
}

/** 映射专业候选。 */
function mapMajorCandidate(payload: Record<string, unknown>): CustomizationMajorCandidate {
  return {
    id: String(payload.id ?? ""),
    name: String(payload.name ?? ""),
    category: toOptionalString(payload.category),
  };
}

/** 映射服务标准草案。 */
function mapCustomizationServiceStandard(
  raw: unknown,
): CustomizationServiceStandardDraft | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const payload = raw as Record<string, unknown>;
  return {
    roundTripTransportNote: toOptionalString(payload.round_trip_transport_note),
    localTransportNote: toOptionalString(payload.local_transport_note),
    accommodationNote: toOptionalString(payload.accommodation_note),
    guideCount: toOptionalNumber(payload.guide_count),
    guideNote: toOptionalString(payload.guide_note),
    includeTickets: toOptionalBoolean(payload.include_tickets),
    ticketNote: toOptionalString(payload.ticket_note),
    includeMeals: toOptionalBoolean(payload.include_meals),
    mealStandards: Array.isArray(payload.meal_standards)
      ? payload.meal_standards.map(String)
      : [],
    mealNote: toOptionalString(payload.meal_note),
    companyVisitCount: toOptionalNumber(payload.company_visit_count),
    companyVisitNote: toOptionalString(payload.company_visit_note),
    intangibleHeritageCount: toOptionalNumber(payload.intangible_heritage_count),
    intangibleHeritageNote: toOptionalString(payload.intangible_heritage_note),
  };
}

/** 映射 summary 分组。 */
function normalizeSummarySection(value: unknown): CustomizationDemandSummarySection | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const payload = value as Record<string, unknown>;
  return {
    sourceTraceSummary: toOptionalString(payload.source_trace_summary),
    demandSummary: toOptionalString(payload.demand_summary) ?? "",
    destinationSummary: toOptionalString(payload.destination_summary),
    durationDays: toOptionalNumber(payload.duration_days),
    travelTime: normalizeRecord(payload.travel_time),
    departureStartDate: toOptionalString(payload.departure_start_date),
    totalCount: toOptionalNumber(payload.total_count),
    studentCount: toOptionalNumber(payload.student_count),
    teacherCount: toOptionalNumber(payload.teacher_count),
    budgetPerPerson: toOptionalString(payload.budget_per_person),
  };
}

/** 映射 customer_requirements 分组。 */
function normalizeCustomerRequirementsSection(
  value: unknown,
): CustomizationDemandCustomerRequirementsSection | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const payload = value as Record<string, unknown>;
  return {
    customerType: toOptionalString(payload.customer_type),
    tripType: toOptionalString(payload.trip_type),
    signType: toOptionalString(payload.sign_type),
    dealProgress: toOptionalString(payload.deal_progress),
    operatingCompany: toOptionalString(payload.operating_company),
    teachingRequirements: toOptionalString(payload.teaching_requirements),
    specialRequirements: toOptionalString(payload.special_requirements),
    schoolPickupRequirement: toOptionalString(payload.school_pickup_requirement),
    invoiceRequirement: toOptionalString(payload.invoice_requirement),
    remark: toOptionalString(payload.remark),
  };
}

/** 映射 teacher_requirements 分组。 */
function normalizeTeacherRequirementsSection(
  value: unknown,
): CustomizationDemandTeacherRequirementsSection | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const payload = value as Record<string, unknown>;
  return {
    teacherMealRequirement: toOptionalString(payload.teacher_meal_requirement),
    teacherTicketRequirement: toOptionalString(payload.teacher_ticket_requirement),
    teacherTransportRequirement: toOptionalString(payload.teacher_transport_requirement),
    teacherAccommodationRequirement: toOptionalString(payload.teacher_accommodation_requirement),
  };
}

/** 映射 relations 分组。 */
function normalizeRelationsSection(value: unknown): CustomizationDemandRelationsSection | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const payload = value as Record<string, unknown>;
  return {
    provinces: normalizeStringArray(payload.provinces),
    cities: normalizeStringArray(payload.cities),
    majorNames: normalizeStringArray(payload.major_names),
    pointNames: normalizeStringArray(payload.point_names),
    stayAreaNames: normalizeStringArray(payload.stay_area_names),
  };
}

/** 映射 display field group。 */
function normalizeDisplayGroups(value: unknown): CustomizationDemandDisplayGroup[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value
    .map((group) => {
      if (!group || typeof group !== "object" || Array.isArray(group)) {
        return null;
      }
      const payload = group as Record<string, unknown>;
      const fields = Array.isArray(payload.fields)
        ? payload.fields
            .map((field) => normalizeDisplayField(field))
            .filter((field): field is CustomizationDemandDisplayField => Boolean(field))
        : [];
      return {
        key: toOptionalString(payload.key) ?? "",
        title: toOptionalString(payload.title) ?? "",
        fields,
      };
    })
    .filter((group): group is CustomizationDemandDisplayGroup => Boolean(group && group.key && group.title));
}

/** 映射 display field。 */
function normalizeDisplayField(value: unknown): CustomizationDemandDisplayField | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const payload = value as Record<string, unknown>;
  const text = toOptionalString(payload.display_text ?? payload.value) ?? "--";
  return {
    key: toOptionalString(payload.key) ?? "",
    label: toOptionalString(payload.label) ?? "",
    kind: toOptionalString(payload.kind),
    value: text,
    displayText: toOptionalString(payload.display_text) ?? undefined,
    hit: normalizeStringArray(payload.hit),
    changed: typeof payload.changed === "boolean" ? payload.changed : undefined,
    span: toOptionalNumber(payload.span),
  };
}

/** 映射 trace_usage。 */
function normalizeTraceUsage(value: unknown): Array<{
  traceRecordId: string;
  usageType: "base" | "supplement" | "conflict_note";
  reason: string;
}> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) {
        return null;
      }
      const payload = item as Record<string, unknown>;
      const traceRecordId = toOptionalString(payload.trace_record_id);
      if (!traceRecordId) {
        return null;
      }
      return {
        traceRecordId,
        usageType:
          payload.usage_type === "supplement" || payload.usage_type === "conflict_note"
            ? payload.usage_type
            : "base",
        reason: toOptionalString(payload.reason) ?? "",
      };
    })
    .filter(
      (
        item,
      ): item is {
        traceRecordId: string;
        usageType: "base" | "supplement" | "conflict_note";
        reason: string;
      } => Boolean(item),
    );
}

/** 将后端变更标记规整为前端可展示结构。 */
function normalizeChangedFields(value: unknown): Record<string, CustomizationChangedField> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const result: Record<string, CustomizationChangedField> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const normalizedKey = toCamelCase(key);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      result[normalizedKey] = { current: raw };
      continue;
    }
    const item = raw as Record<string, unknown>;
    result[normalizedKey] = {
      previous: item.previous,
      current: item.current,
      traceRecordIds: Array.isArray(item.trace_record_ids)
        ? item.trace_record_ids.map(String)
        : Array.isArray(item.traceRecordIds)
          ? item.traceRecordIds.map(String)
          : [],
      reason: typeof item.reason === "string" ? item.reason : "",
    };
  }
  return result;
}

/** 将值规整为可选字符串。 */
function toOptionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).trim();
  return text || undefined;
}

/** 将值规整为可选数字。 */
function toOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

/** 将值规整为可选布尔值。 */
function toOptionalBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") {
    return value;
  }
  if (value === 1 || value === "1" || value === "true") {
    return true;
  }
  if (value === 0 || value === "0" || value === "false") {
    return false;
  }
  return undefined;
}

/** 将未知值规整为字符串数组。 */
function normalizeStringArray(value: unknown): string[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  const text = String(value).trim();
  return text ? [text] : [];
}

/** 将旧系统行程预览中的拼接名称规整为数组。 */
function normalizeRoutePreviewNameArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeRoutePreviewNameArray);
  }
  const text = String(value ?? "").trim();
  if (!text) {
    return [];
  }
  return text
    .replace(/\|\|/g, "、")
    .replace(/\r?\n/g, "、")
    .replace(/[，,；;]/g, "、")
    .split("、")
    .map((item) => item.trim())
    .filter((item, index, all) => Boolean(item) && all.indexOf(item) === index);
}

/** 将后端 snake_case 字段名转换为前端 camelCase 字段名。 */
function toCamelCase(value: string): string {
  return value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}
